import { LegacyRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PlusCircleOutlined } from "@ant-design/icons";
import { Col, Form, type FormListFieldData, Input, Radio, RadioChangeEvent, Select } from "antd";
import { clsx } from "clsx";
import assignWith from "lodash/assignWith";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import {
    RVIcon,
    RVIconSizeOptions,
    RVFormFloatingLabelWrap,
    RVFormItem,
    RVEmojiPicker,
    RVButton,
    RVFormPageLayout,
    RVLoading,
} from "@regal-voice/ui";
import { AppPagePaths } from "App/routes/paths";
import {
    generateFormItemRequiredRules,
    generateNonEmptyListValidator,
} from "Components/FormItems/Shared/Validators/InputValidators/InputValidators";
import { DrawerPanelIcon } from "Components/shared/DrawerPanelIcon";
import { useEventNames } from "Pages/audience/Pages/SegmentsPage/Pages/SegmentEditPage/ConditionsList/Shared/useEventNames";
import { caseInsensitiveFilter } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/Components/JourneyActionsPanel/NodeEditorPanel/shared";
import { FormSkeleton } from "Pages/settings/Pages/UserManagement/Components/FormSkeleton/FormSkeleton";
import { useTeamsData } from "Pages/settings/Pages/UserManagement/Hooks/useTeamData";
import { SettingsPaths } from "Pages/settings/paths";
import { ToggleCollapsiblePanelButton, useKnownPanels } from "Utils/CollapsiblePanels";

import { useEventData } from "../../Hooks/useEventData";
import { useEventDefinitionMutation } from "../../Hooks/useEventDefinitionMutation";
import { UpsertEventDefinitionInputData } from "../../mutations";

import type { EventSchemaOptionEntry } from "ServerData/EventSchema/eventSchemaTypes";

import styles from "./EventDefinitionForm.module.scss";

const COLORS = {
    Pink: "red-1",
    Orange: "peach-2",
    Yellow: "gold-2",
    Green: "green-1",
    Blue: "blue-1",
};

const colorOptions = Object.entries(COLORS).map(([label, color]) => ({
    value: color,
    label: (
        <div className={styles.colorOption} key={color}>
            <div className={clsx(styles.colorSwatch, styles[color])} />
            <div>{label}</div>
        </div>
    ),
}));

export default function EventDefinitionForm(): JSX.Element {
    const { settings: settingsPanel } = useKnownPanels();

    const [form] = Form.useForm();
    const params = useParams<{ eventType: string; eventName: string }>();
    const eventType = params.eventType as string;
    const eventName = params.eventName as string;
    // @TODO: determine if we can replace tis with useContactAttributesFullListQuery or useEventPropertiesQuery.
    const { eventSchema } = useEventNames(eventType, eventName);
    const { teams } = useTeamsData();
    const [upsertEventDefinitionInputData, setUpsertEventDefinitionInputData] = useState<
        UpsertEventDefinitionInputData | undefined
    >();
    const { loading, events, refetch } = useEventData();
    const navigate = useNavigate();
    const { key: previousPageKey } = useLocation();

    const addAttribute = useRef<((defaultValue?: any, insertIndex?: number | undefined) => void) | null>(null);
    const [shouldScrollIntoViewByIndex, setShouldScrollIntoViewByIndex] = useState<number>();

    const [displayEmoji, setDisplayEmoji] = useState<string | null>(null);

    const backCallback = useCallback(() => {
        if (previousPageKey) {
            return navigate(-1);
        }
        return navigate(`${AppPagePaths.settings}/${SettingsPaths.eventManagement}`, { replace: true });
    }, [previousPageKey, navigate]);

    const backTo = previousPageKey ? ".." : `${AppPagePaths.settings}/${SettingsPaths.eventManagement}`;

    useEffect(() => {
        const definition = events.find(
            (event) =>
                event.eventType === decodeURIComponent(eventType) && event.eventName === decodeURIComponent(eventName)
        );
        if (!loading && !definition) {
            backCallback();
        }
        if (definition) {
            setUpsertEventDefinitionInputData({
                showInFeed: definition.showInFeed,
                displayName: definition.displayName ?? null,
                // we want Blue color to be the default
                displayColor: definition.displayColor ?? COLORS.Blue,
                displayEmoji: definition.displayEmoji ?? null,
                teamIds: definition.teams?.map((team) => team.id) ?? [],
                attributes: definition.attributes,
            });
        }
    }, [events, eventType, eventName, backCallback, loading, setUpsertEventDefinitionInputData]);

    // use state instead of getFieldValue because form is not submitted on every change
    const [showInFeed, setShowInFeed] = useState<boolean>(false);
    const [showTeams, setShowTeams] = useState<boolean>(false);
    useEffect(() => {
        setShowInFeed(upsertEventDefinitionInputData?.showInFeed ?? false);
        setShowTeams(!!upsertEventDefinitionInputData?.teamIds?.length);
        setDisplayEmoji(upsertEventDefinitionInputData?.displayEmoji ?? null);
    }, [upsertEventDefinitionInputData, setShowInFeed, setShowTeams, setDisplayEmoji]);

    const { isMutating, upsertEventDefinition } = useEventDefinitionMutation();

    const handleSave = useCallback(
        async (data: UpsertEventDefinitionInputData): Promise<void> => {
            const upsert = assignWith(
                {
                    eventType,
                    eventName,
                },
                {
                    ...data,
                    displayEmoji,
                    attributes: data.attributes?.map((attribute: { displayName: any; attributeKey: any }) => ({
                        displayName: attribute.displayName,
                        attributeKey: attribute.attributeKey,
                    })),
                    teamIds: showTeams ? data.teamIds : [],
                },
                (_, value) => {
                    if (typeof value === "string") {
                        if (!value.length) {
                            return null;
                        }
                    }
                    return value;
                }
            );
            await upsertEventDefinition(upsert);
            refetch();
        },
        [upsertEventDefinition, refetch, eventType, eventName, displayEmoji, showTeams]
    );

    const submit = useCallback(async () => {
        await form.validateFields();
        form.submit();
    }, [form]);

    const onEventVisibilityChange = useCallback(
        (event: RadioChangeEvent) => {
            setShowTeams(event.target.value);
        },
        [setShowTeams]
    );

    const generateAddAttributeAndScrollIntoViewFn = useCallback(
        (add: (defaultValue?: any, insertIndex?: number | undefined) => void, fields: FormListFieldData[]) => {
            return () => {
                setShouldScrollIntoViewByIndex(fields.length);
                add({
                    orderIndex: fields.length + 1,
                    id: undefined,
                    weight: undefined,
                    type: undefined,
                });
            };
        },
        []
    );

    const generateRemoveFieldFn = useCallback(
        (remove: (index: number | number[]) => void, fieldName: number | number[]) => () => remove(fieldName),
        []
    );

    const teamOptions = useMemo(
        () =>
            teams.map(({ id, name }) => ({
                label: name,
                value: id,
            })),
        [teams]
    );

    if (loading || !upsertEventDefinitionInputData) {
        return <RVLoading />;
    }

    const title = `Edit ${decodeURIComponent(eventName)}`;
    const footer = (
        <footer className={styles.formFooter} data-testid="event-definition-form-footer">
            <RVButton
                onClick={submit}
                type="primary"
                htmlType="submit"
                loading={isMutating}
                disabled={isMutating}
                data-testid="save-event-definition"
            >
                Save Event
            </RVButton>
        </footer>
    );

    const content = (
        <>
            {isMutating ? (
                <FormSkeleton fieldsCount={2} />
            ) : (
                <Col span="12">
                    <Form
                        className={styles.formContainer}
                        form={form}
                        layout="vertical"
                        initialValues={upsertEventDefinitionInputData}
                        onFinish={handleSave}
                    >
                        <RVFormItem
                            label="Show this event in Agent Desktop activity feed?"
                            description="If enabled, this event will be shown in the activity feed chronologically among other enabled events and conversations with a contact. Events may take a few minutes to appear. You can choose what title and properties will be shown."
                            isFullPageForm
                            name="showInFeed"
                            rules={[{ required: true, message: "Show in feed is a required field." }]}
                        >
                            <Radio.Group className="selection-radio" size="small">
                                <Radio value={false} onChange={(e) => e.target.checked && setShowInFeed(false)}>
                                    Don’t Show in Agent Desktop Feed
                                </Radio>
                                <Radio value={true} onChange={(e) => e.target.checked && setShowInFeed(true)}>
                                    Add to Agent Desktop Feed
                                </Radio>
                            </Radio.Group>
                        </RVFormItem>

                        {showInFeed && (
                            <>
                                <RVFormItem
                                    label="Event Visibility"
                                    description="Specify whether this event will be visible to all users or specific teams."
                                    required
                                    isFullPageForm
                                >
                                    <Radio.Group
                                        className="selection-radio"
                                        size="small"
                                        onChange={onEventVisibilityChange}
                                        value={showTeams}
                                        defaultValue={showTeams}
                                    >
                                        <Radio value={false}>All Teams</Radio>
                                        <Radio value={true}>Specific Teams</Radio>
                                    </Radio.Group>
                                </RVFormItem>

                                {showTeams ? (
                                    <RVFormItem
                                        label="Team Selection"
                                        description="Specify which teams this event will be visible to."
                                        name="teamIds"
                                        rules={[generateNonEmptyListValidator("Please select at least one team.")]}
                                        isFullPageForm
                                    >
                                        <Select
                                            mode="multiple"
                                            showArrow={true}
                                            placeholder="Team Selection"
                                            optionFilterProp="label"
                                            options={teamOptions}
                                        />
                                    </RVFormItem>
                                ) : (
                                    <RVFormItem hidden name="teamIds">
                                        <Input type="hidden" value={[]} />
                                    </RVFormItem>
                                )}

                                <RVFormItem
                                    label="Display Name for Event"
                                    description="The name that agents will see for this event on the Agent Desktop Activity Feed."
                                    isFullPageForm
                                    name="displayName"
                                    rules={[{ required: showInFeed, message: "Display Name is required." }]}
                                >
                                    <Input
                                        size="large"
                                        autoComplete="off"
                                        data-testid="displayName"
                                        placeholder="Display Name for Event"
                                        prefix={<RVEmojiPicker onChange={setDisplayEmoji} value={displayEmoji} />}
                                    />
                                </RVFormItem>

                                <RVFormItem
                                    label="Display Color for Event"
                                    description="Customize the background color of the wrapper for this event that agents will see on the Agent Desktop Activity Feed."
                                    isFullPageForm
                                    name="displayColor"
                                >
                                    <Select
                                        size="large"
                                        // I'm not sure if this is a failing of antd's types or why the parent element is optional here.
                                        // disabling this rule as part of clean up
                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        getPopupContainer={(trigger) => trigger.parentElement!}
                                        options={colorOptions}
                                    />
                                </RVFormItem>

                                <RVFormItem
                                    label="Event Properties to Display"
                                    description="Determine which properties of the event and the ordering you want to display to agents."
                                    isFullPageForm
                                >
                                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                                    <DndProvider backend={HTML5Backend}>
                                        <Form.List name="attributes">
                                            {(fields, { add, remove, move }, { errors }) => {
                                                addAttribute.current = add;
                                                return (
                                                    <div className="floating-design">
                                                        {fields.map((field: FormListFieldData) => (
                                                            <AttributesPanel
                                                                key={field.key}
                                                                dragIndex={field.name}
                                                                dragMove={move}
                                                                field={field}
                                                                removeCondition={generateRemoveFieldFn(
                                                                    remove,
                                                                    field.name
                                                                )}
                                                                shouldScrollIntoViewByIndex={
                                                                    shouldScrollIntoViewByIndex
                                                                }
                                                                eventSchema={eventSchema}
                                                            />
                                                        ))}
                                                        <Form.ErrorList errors={errors} />

                                                        <RVButton
                                                            data-testid="add-event-definition-attribute"
                                                            type="link"
                                                            icon={<PlusCircleOutlined />}
                                                            className={"add-item-link-button"}
                                                            onClick={generateAddAttributeAndScrollIntoViewFn(
                                                                add,
                                                                fields
                                                            )}
                                                        >
                                                            Add Property
                                                        </RVButton>
                                                    </div>
                                                );
                                            }}
                                        </Form.List>
                                    </DndProvider>
                                </RVFormItem>
                            </>
                        )}
                    </Form>
                </Col>
            )}
        </>
    );

    return (
        <RVFormPageLayout
            id={`${eventType}-${eventName}`}
            title={title}
            wide
            prefix={<ToggleCollapsiblePanelButton panelName={settingsPanel.name} direction="left" />}
            backTo={backTo}
            backText="Back to Event Management"
            footer={footer}
        >
            {content}
        </RVFormPageLayout>
    );
}

type AttributesPanelProps = {
    dragIndex: number;
    dragMove: (from: number, to: number) => void;
    field: FormListFieldData;
    removeCondition: () => void;
    eventSchema: EventSchemaOptionEntry;
    shouldScrollIntoViewByIndex?: number;
};

export function AttributesPanel({
    dragIndex,
    dragMove,
    field,
    removeCondition,
    eventSchema,
    shouldScrollIntoViewByIndex,
}: AttributesPanelProps) {
    const ref = useRef<HTMLDivElement>(null);
    const previewRef = useRef();
    const [, drag, preview] = useDrag({
        type: "AttributeTemplatePanel",
        item: { index: dragIndex },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const shouldScroll = shouldScrollIntoViewByIndex == field.name;
    const [{ isOver, dropClassName }, drop] = useDrop({
        accept: "AttributeTemplatePanel",
        collect: (monitor) => {
            if (typeof dragIndex === "undefined") {
                return {};
            }

            const { index } = monitor.getItem() || {};

            return {
                isOver: monitor.isOver({ shallow: true }),
                dropClassName: index < dragIndex ? styles.dropDownward : styles.dropUpward,
            };
        },
        drop: (item: any) => {
            if (typeof dragIndex !== "undefined" && dragMove) {
                dragMove(item.index, dragIndex);
            }
        },
    });

    drag(ref);
    if (preview) {
        drop(preview(previewRef));
    }

    useEffect(() => {
        if (shouldScroll && ref.current) {
            ref.current?.scrollIntoView();
        }
    }, [ref, shouldScroll]);

    return (
        <div
            ref={previewRef as unknown as LegacyRef<HTMLDivElement>}
            data-testid="event-definition-attribute-draggable-container"
            className={clsx(
                "draggable-drawer-panel full-with-input-field",
                dropClassName && isOver ? dropClassName : null
            )}
        >
            <AttributeTemplatePanel
                autoFocus={shouldScroll}
                dragHandleRef={ref}
                field={field}
                removeCondition={removeCondition}
                eventSchema={eventSchema}
            />
        </div>
    );
}

const AttributeKeyRequiredRules = generateFormItemRequiredRules("Attribute Key");
const DisplayNameRequiredRules = generateFormItemRequiredRules("Display Name");

type AttributeTemplatePanelProp = {
    dragHandleRef: LegacyRef<HTMLDivElement>;
    field: FormListFieldData;
    removeCondition: () => void;
    autoFocus: boolean;
    eventSchema: EventSchemaOptionEntry;
};

export function AttributeTemplatePanel({
    dragHandleRef,
    field,
    removeCondition,
    eventSchema,
}: AttributeTemplatePanelProp) {
    const draggable = !!dragHandleRef;

    const options = useMemo(
        () =>
            Object.entries(eventSchema)
                .filter(([_key, schema]) => schema.fieldType !== "object")
                .filter(([key]) => key.startsWith("properties."))
                .map(([key, schema]) => ({
                    value: key,
                    label: (
                        <>
                            <div>{key}</div>
                            <div className={styles.attributeType}>Data Type: {schema.fieldType}</div>
                        </>
                    ),
                }))
                .sort((o1, o2) => (o1.value > o2.value ? 1 : -1)),
        [eventSchema]
    );

    return (
        <div
            className={clsx(styles.attributePanel)}
            {...(draggable ? { ref: dragHandleRef } : {})}
            data-testid="event-definition-attribute"
        >
            <RVFormItem hidden name={[field.name, "id"]}>
                <Input readOnly data-testid="event-definition-attribute-id" />
            </RVFormItem>
            <RVIcon icon="drag-handle" size={RVIconSizeOptions.XSMALL} className={styles.actionIcon} />

            <RVFormFloatingLabelWrap
                label="Property"
                name={[field.name, "attributeKey"]}
                containerClassName={styles.attributeProperty}
                rules={AttributeKeyRequiredRules}
            >
                <Select
                    showSearch
                    filterOption={caseInsensitiveFilter}
                    data-testid="event-definition-attribute-type"
                    // I'm not sure if this is a failing of antd's types or why the parent element is optional here.
                    // disabling this rule as part of clean up
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    getPopupContainer={(trigger) => trigger.parentElement!}
                    options={options}
                />
            </RVFormFloatingLabelWrap>

            <RVFormFloatingLabelWrap
                data-testid="event-definition-attribute-display-name"
                label="Display Name"
                name={[field.name, "displayName"]}
                containerClassName={styles.attributeDisplayName}
                rules={DisplayNameRequiredRules}
            >
                <Input.TextArea autoSize={{ minRows: 1 }} size="large"></Input.TextArea>
            </RVFormFloatingLabelWrap>

            <DrawerPanelIcon icon="trash" danger={true} onClick={removeCondition} className={styles.actionIcon} />
        </div>
    );
}
